import { createMachine, assign, raise } from "xstate";
import {
	initStep,
	isOnOrganicSoil,
	mainActivityFocus,
	hectares,
	previousLandUse,
	areaWithoutProject,
	livestockFeedOrigin,
	fishFeedOrigin,
	includesInputs,
	usesEnergy,
	fisheriesSize,
	usesValueChain,
} from "./setupAssistantUtils";
import { FormEvents, FormStates } from "./setupAssistant.types"
import type {
	FormMachineEvents,
	Context,
	SetupAssistantForm,
} from "./setupAssistant.types";
import { FEModules } from "@utils/moduleList";
import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch } from "@app/hooks";
import { setProgressiveModules } from "@features/setupAssistant/setupAssistantSlice";
import { useGetChangeRatesQuery } from "@hooks/index";
import { useHandleProjectState } from "@pages/project/useHandleProjectState";
import { createInitialValues, createValidationSchema } from "./setupAssistantMachine.utils";
import { FormNavigationProps, MachineProps } from "./setupAssistantMachine.types";
import useErrorMessage from "@utils/useErrorMessage";

// Raises a PUSH_MODULES event carrying the modules from the current NEXT event,
// tagged with the state that generated them.
const pushModulesAction = (generatedBy: FormStates) =>
	raise<Context, FormMachineEvents>((_, { modules }) => ({
		type: FormEvents.PUSH_MODULES,
		modules,
		generatedBy,
	}));

// BACK guard: true when the step below the top of the schema stack is `step`.
const prevStepIs = (step: FormStates) => ({ schema }: Context) =>
	schema[schema.length - 2].step === step;

// Standard BACK transition to a previous state, gated by the schema stack.
const backTo = (step: FormStates) => ({
	target: [step],
	cond: prevStepIs(step),
	actions: ["removeLast", "pushValues"],
});

/* STATE MACHINE */
export const formMachine = createMachine<Context, FormMachineEvents>(
	{
		id: "setup-assistant",
		context: {
			schema: [],
			values: {},
			modules: [],
		},
		initial: FormStates.initInfo,
		states: {
			[FormStates.initInfo]: {
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.mainActivityFocus],
						actions: ["addMainActivity", "pushValues"],
					},
					[FormEvents.SET_INIT_VALUES]: { actions: ["addInitalInfo"] },
					[FormEvents.SKIP]: {
						target: [FormStates.finish],
						actions: ["pushValues"],
					},
				},
			},
			[FormStates.mainActivityFocus]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.mainActivityFocus,
						),
				}),
				on: {
					[FormEvents.NEXT]: [
						{
							target: [FormStates.hectares],
							actions: ["addHectares", "pushValues", pushModulesAction(FormStates.mainActivityFocus)],
							cond: (_, { next }) => next === FormStates.hectares,
						},
						{
							target: [FormStates.isOrganicSoil],
							actions: ["addOrganicSoil", "pushValues", pushModulesAction(FormStates.mainActivityFocus)],
							cond: (_, { next }) => next === FormStates.isOrganicSoil,
						},
						{
							target: [FormStates.livestockFeedOrigin],
							actions: ["addLivestock", "pushValues", pushModulesAction(FormStates.isOrganicSoil)],
							cond: (_, { next }) => next === FormStates.livestockFeedOrigin,
						},
						{
							target: [FormStates.fisheriesSize],
							actions: ["addFisheriesSize", "pushValues", pushModulesAction(FormStates.isOrganicSoil)],
							cond: (_, { next }) => next === FormStates.fisheriesSize,
						},
						{
							target: [FormStates.fishFeedOrigin],
							actions: ["addFishFeed", "pushValues", pushModulesAction(FormStates.isOrganicSoil)],
							cond: (_, { next }) => next === FormStates.fishFeedOrigin,
						},
						{
							target: [FormStates.includesInputs],
							actions: ["addIncludeInputs", "pushValues", pushModulesAction(FormStates.isOrganicSoil)],
							cond: (_, { next }) => next === FormStates.includesInputs,
						},
						{
							target: [FormStates.usesValueChain],
							actions: ["addUsesValueChain", "pushValues", pushModulesAction(FormStates.mainActivityFocus)],
							cond: (_, { next }) => next === FormStates.usesValueChain,
						},
					],
					[FormEvents.BACK]: {
						target: [FormStates.initInfo],
						actions: ["removeLast", "pushValues"],
					},
				},
			},
			[FormStates.isOrganicSoil]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.isOrganicSoil,
						),
				}),
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.hectares],
						actions: [
							pushModulesAction(FormStates.isOrganicSoil),
							"addHectares",
							"pushValues",
						],
					},
					[FormEvents.BACK]: {
						target: [FormStates.mainActivityFocus],
						actions: ["removeLast", "pushValues"],
					},
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.hectares]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter((el) => el.generatedBy !== FormStates.hectares),
				}),
				on: {
					[FormEvents.NEXT]: [
						{
							target: [FormStates.previousLandUse],
							actions: ["addPreviousLandUse", "pushValues"],
							cond: ({ values }) => values.mainActivityFocus !== FEModules.Waterbodies,
						}, //means: if mainActivityFocus is Waterbodies
						{
							target: [FormStates.usesEnergy],
							actions: ["addEnergyUse", "pushValues"],
							cond: ({ values }) => values.mainActivityFocus === FEModules.Waterbodies,
						},
					],
					[FormEvents.BACK]: [
						backTo(FormStates.isOrganicSoil),
						backTo(FormStates.mainActivityFocus),
					],
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.previousLandUse]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.previousLandUse,
						),
				}),
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.areaWithoutProject],
						actions: [
							"addAreaWithoutProject",
							"pushValues",
							pushModulesAction(FormStates.previousLandUse),
						],
					},
					[FormEvents.BACK]: {
						target: [FormStates.hectares],
						actions: ["removeLast", "pushValues"],
					},
				},
			},
			[FormStates.areaWithoutProject]: {
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.includesInputs],
						actions: [
							"addIncludeInputs",
							"pushValues",
							pushModulesAction(FormStates.areaWithoutProject),
						],
					},
					[FormEvents.BACK]: {
						target: [FormStates.previousLandUse],
						actions: ["removeLast", "pushValues"],
					},
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.livestockFeedOrigin]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.livestockFeedOrigin,
						),
				}),
				on: {
					[FormEvents.NEXT]: [
						{
							target: [FormStates.includesInputs],
							actions: ["addIncludeInputs", "pushValues", pushModulesAction(FormStates.livestockFeedOrigin)],
						},
					],
					[FormEvents.BACK]: {
						target: [FormStates.mainActivityFocus],
						actions: ["removeLast", "pushValues"],
					},
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.fishFeedOrigin]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.fishFeedOrigin,
						),
				}),
				on: {
					[FormEvents.NEXT]: [
						{
							target: [FormStates.includesInputs],
							actions: ["addIncludeInputs", "pushValues", pushModulesAction(FormStates.fishFeedOrigin)],
						},
					],
					[FormEvents.BACK]: {
						target: [FormStates.mainActivityFocus],
						actions: ["removeLast", "pushValues"],
					},
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.fisheriesSize]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.fisheriesSize,
						),
				}),
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.usesEnergy],
						actions: ["addEnergyUse", "pushValues", pushModulesAction(FormStates.fisheriesSize)],
					},
					[FormEvents.BACK]: {
						target: [FormStates.mainActivityFocus],
						actions: ["removeLast", "pushValues"],
					},
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.includesInputs]: {
				entry: assign({
					modules: (ctx) =>
						ctx.modules.filter(
							(el) => el.generatedBy !== FormStates.includesInputs,
						),
				}),
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.usesValueChain], //FormStates.usesVC and from VC we'll go to finish
						actions: ["addUsesValueChain", "pushValues", pushModulesAction(FormStates.includesInputs)],
					},
					[FormEvents.BACK]: [
						backTo(FormStates.mainActivityFocus),
						backTo(FormStates.livestockFeedOrigin),
						backTo(FormStates.fishFeedOrigin),
						backTo(FormStates.areaWithoutProject),
					],
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.usesEnergy]: {
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.finish],
						actions: ["pushValues", pushModulesAction(FormStates.usesEnergy)],
					},
					[FormEvents.BACK]: [
						backTo(FormStates.fisheriesSize),
						backTo(FormStates.hectares),
					],
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.usesValueChain]: {
				on: {
					[FormEvents.NEXT]: {
						target: [FormStates.finish],
						actions: ["pushValues", pushModulesAction(FormStates.usesValueChain)],
					},
					[FormEvents.BACK]: [
						backTo(FormStates.includesInputs),
						backTo(FormStates.mainActivityFocus),
					],
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
				},
			},
			[FormStates.finish]: {
				on: {
					[FormEvents.PUSH_MODULES]: { actions: "pushModules" },
					[FormEvents.BACK]:
						[
							{
								target: [FormStates.initInfo],
								actions: "returnToInitial",
							},
						]

				},
			},
		},
		predictableActionArguments: true,
	},
	{
		actions: {
			returnToInitial: assign({
				schema: (context) => {
					return [context.schema[0]];
				},
			}),
			pushModules: assign({
				modules: (context, { generatedBy, modules }) => {
					let tempMods = [...context.modules];
					modules?.forEach((mod) => {
						const index = tempMods.findIndex((el) => el.module === mod);
						if (index === -1)
							tempMods = [
								...tempMods,
								{
									module: mod,
									generatedBy: generatedBy ?? FormStates.initInfo,
								},
							];
					});
					return tempMods;
				},
			}),
			pushValues: assign({
				values: (context, { values }) => ({ ...context.values, ...values }),
			}),
			addInitalInfo: assign({
				schema: (context, { schemaStep }) =>
					schemaStep ? [schemaStep] : context.schema,
			}),
			addOrganicSoil: assign({
				schema: (context) => [...context.schema, isOnOrganicSoil],
			}),
			addMainActivity: assign({
				schema: (context) => [...context.schema, mainActivityFocus],
			}),
			addHectares: assign({
				schema: (context) => [...context.schema, hectares],
			}),
			addPreviousLandUse: assign({
				schema: (context) => [...context.schema, previousLandUse],
			}),
			addLivestock: assign({
				schema: (context) => [...context.schema, livestockFeedOrigin],
			}),
			addIncludeInputs: assign({
				schema: (context) => [...context.schema, includesInputs],
			}),
			addFishFeed: assign({
				schema: (context) => [...context.schema, fishFeedOrigin],
			}),
			addFisheriesSize: assign({
				schema: (context) => [...context.schema, fisheriesSize],
			}),
			addAreaWithoutProject: assign({
				schema: (context) => [...context.schema, areaWithoutProject],
			}),
			addEnergyUse: assign({
				schema: (context) => [...context.schema, usesEnergy],
			}),
			addUsesValueChain: assign({
				schema: (context) => [...context.schema, usesValueChain],
			}),
			removeLast: assign({
				schema: (context) => {
					context.schema.pop();
					return [...context.schema];
				},
			}),
		},
		guards: {},
	},
);

export function useFormMachine(): MachineProps {
	const dispatch = useAppDispatch();
	const [state, send] = useMachine(formMachine);
	const { data: changeRates, isError: isChangeRatesError, error: changeRatesError } = useGetChangeRatesQuery();
	const { project, isLoading } = useHandleProjectState();

	// Initialize form when project and change rates are available
	useEffect(() => {
		if (project && state.value === FormStates.initInfo && changeRates && !isLoading) {
			const defaultChangeRate = changeRates.find(rate => rate.name === "linear");
			const initialValues = createInitialValues(project, defaultChangeRate);
			const validationSchema = createValidationSchema(project);

			send({
				type: FormEvents.SET_INIT_VALUES,
				schemaStep: { ...initStep, initialValues, validationSchema },
			});
		}
	}, [project, send, changeRates]);

	// Update modules in global state
	useEffect(() => {
		if (state.context.modules.length > 0) {
			const modules = state.context.modules.map((mod) => mod.module);
			dispatch(setProgressiveModules(modules));
		}
	}, [state.context.modules, dispatch]);

	// Memoized state values
	const currentState = useMemo(() => state.value.toString(), [state]);
	const isFinal = useMemo(() => state.value === "finish", [state]);
	const history = useMemo(() => state.history, [state]);
	const canSkip = useMemo(() => state.can({ type: FormEvents.SKIP }), [state]);
	const canBack = useMemo(
		() => state.can({ type: FormEvents.BACK, prev: history?.value }),
		[state, history]
	);

	// Navigation callbacks
	const goBack = useCallback(
		({ values }: Pick<FormNavigationProps, 'values'>) => {
			send({ type: FormEvents.BACK, prev: history?.value, values });
		},
		[send, history]
	);

	const goNext = useCallback(
		({ next, values, modules }: FormNavigationProps) => {
			send({ type: FormEvents.NEXT, next, values, modules });
		},
		[send]
	);

	const skip = useCallback(
		(values: SetupAssistantForm) => send({ type: FormEvents.SKIP, values }),
		[send]
	);

	// Error handling
	const { errorMsg } = useErrorMessage({
		error: changeRatesError,
		isError: isChangeRatesError
	});

	return {
		// Navigation methods
		goNext,
		goBack,
		skip,

		// State data
		schema: state.context.schema,
		values: state.context.values,
		modules: [...state.context.modules],
		currentState,

		// Navigation flags
		canBack,
		canSkip,
		isFinal,

		// Error handling
		isError: isChangeRatesError,
		errorMsg
	};
}
